import { z } from "zod";
import { Id, NonEmpty, Url } from "../base";
import { AvatarSchema, DiscordAccountSchema, XAccountSchema } from "../user";
import { communityStatusValues, communityPermissionValues, COMMUNITY_SOCIAL_PLATFORMS, } from "../constant_values";
import { QuestSubmissionSchema } from "./campaign/quest";
import { TimestampInputSchema } from "../epoch";
import { CommunityTeamMemberRecordSchema } from "./team";
//#region Helpers
const emptyToUndefined = (value) => {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== "string") {
        throw new Error("Expected string");
    }
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
};
const optionalUrl = z.preprocess(emptyToUndefined, Url.max(2048).optional());
function isDiscordInviteUrl(value) {
    try {
        const url = new URL(value);
        const host = url.hostname.toLowerCase();
        if (host === "discord.gg" || host === "www.discord.gg") {
            return url.pathname.length > 1;
        }
        if (host === "discord.com" || host === "www.discord.com") {
            return /^\/invite\/[^/]+\/?$/.test(url.pathname);
        }
        return false;
    }
    catch {
        return false;
    }
}
export const DiscordInviteUrlSchema = z
    .string()
    .trim()
    .url("A valid Discord invite URL is required")
    .max(2048, "URL is too long")
    .refine(isDiscordInviteUrl, "A Discord invite URL is required");
export const CommunityDiscordServerSchema = z
    .object({
    guildId: NonEmpty.max(64),
    name: NonEmpty.max(120),
    iconHash: z.string().nullable().optional(),
    owner: z.boolean().optional(),
    permissions: z.string().optional(),
    connectedByUserId: Id.optional(),
})
    .strict();
//#endregion
//#region Schema
const customSocialLinkSchema = z
    .object({
    label: NonEmpty.max(120),
    emoji: NonEmpty.max(8),
    url: Url.max(2048),
    saved: z.boolean().optional(),
})
    .strict();
const customSocialsSchema = z
    .object({
    website: optionalUrl,
    telegram: optionalUrl,
    medium: optionalUrl,
    youtube: optionalUrl,
    github: optionalUrl,
    customLinks: z.array(customSocialLinkSchema).optional(),
})
    .partial()
    .strict();
export const verifiedSocialSchema = z
    .object({
    twitter: z
        .object({
        url: optionalUrl,
        account: XAccountSchema.nullable().optional(),
        verifiedAt: TimestampInputSchema.optional(),
    })
        .partial()
        .optional(),
    discord: z
        .object({
        url: z.preprocess(emptyToUndefined, DiscordInviteUrlSchema.optional()),
        account: DiscordAccountSchema.nullable().optional(),
        server: CommunityDiscordServerSchema.optional(),
        verifiedAt: TimestampInputSchema.optional(),
    })
        .partial()
        .optional(),
})
    .strict();
// LOGO
const LogoSchema = AvatarSchema;
// COMMUNITY PROFILE
export const CommunityStatusSchema = z.enum(communityStatusValues);
export const CommunityProfileServerOwnedSchema = z.strictObject({
    // server-owned flags
    id: Id,
    status: CommunityStatusSchema,
    verifiedSocials: verifiedSocialSchema.optional(),
    didPublishAnyCampaign: z.boolean().optional(), //TODO make sure to update when publishing
    // server-owned timestamps
    createdAt: TimestampInputSchema,
    updatedAt: TimestampInputSchema,
});
const CommunityProfileUserEditableSchema = z.strictObject({
    name: NonEmpty,
    logo: LogoSchema,
    bio: z.preprocess(emptyToUndefined, z.string().optional()),
    unverifiedSocials: customSocialsSchema.optional(),
    sector: z.preprocess(emptyToUndefined, z.string().optional()),
    blockchain: z.preprocess(emptyToUndefined, z.string().optional()),
});
export const CommunityProfileUserOwnedSchema = CommunityProfileUserEditableSchema.extend({
    id: Id,
});
// COMMUNITY PROFILE RECORD SAVED ON DB
export const CommunityProfileRecordSchema = CommunityProfileUserEditableSchema.extend(CommunityProfileServerOwnedSchema.shape);
// PROFILE PUBLIC VIEW
export const CommunityProfilePublicViewSchema = CommunityProfileRecordSchema;
// CREATE PROFILE
export const CommunityProfileCreateSchema = CommunityProfileUserEditableSchema;
// PROFILE PATCH
export const CommunityProfilePatchSchema = CommunityProfileUserOwnedSchema.partial();
// ROOT (how community data is stored on db)
export const CommunityRecordSchema = z.object({
    id: Id,
    ownerId: Id,
    profile: CommunityProfileRecordSchema,
    teamMembers: z.array(CommunityTeamMemberRecordSchema).optional(),
    participantsCount: z.number().default(0),
    followersCount: z.number().default(0),
    totalRewards: z.number().default(0),
    createdAt: TimestampInputSchema,
    updatedAt: TimestampInputSchema.nullable(),
});
// ROOT PUBLIC GET
export const CommunityViewPublicSchema = CommunityRecordSchema.omit({
    ownerId: true,
}).extend({
    profile: CommunityProfilePublicViewSchema,
});
// ROOT CREATE COMMUNITY
export const CommunityCreateSchema = CommunityRecordSchema.extend({
    profile: CommunityProfileCreateSchema,
}).omit({
    id: true,
    participantsCount: true,
    followersCount: true,
    totalRewards: true,
});
// ROOT PATCH COMMUNITY -- OWNERSHIP TRANSFER
export const CommunityPatchSchema = z.strictObject({
    newOwnerId: Id,
    keepPreviousOwnerAsAdmin: z.boolean().optional(),
});
// TEAM MEMBERS
export const RawMemberSchema = z.object({
    userId: Id,
    fallbackName: NonEmpty,
    fallbackEmail: NonEmpty,
    fallbackAvatar: NonEmpty,
    fallbackWalletAddress: NonEmpty,
    blocks: z.number(),
    questsCompleted: z.number(),
    completedQuests: z.array(QuestSubmissionSchema),
});
export const CommunityPermissionSchema = z.enum(communityPermissionValues);
export const SocialConnectSchema = z.object({
    url: z
        .string()
        .trim()
        .url("A valid URL is required")
        .max(2048, "URL is too long"),
});
export const CommunityDiscordServerConnectSchema = z
    .object({
    guildId: NonEmpty.max(64),
    inviteUrl: DiscordInviteUrlSchema,
})
    .strict();
export const CommunitySocialPlatformSchema = z.enum(COMMUNITY_SOCIAL_PLATFORMS);
//#endregion Schema
//#region Types
/**
 * The second step of uploading logo
 */
export const LogoCompleteSchema = z.object({
    objectPath: NonEmpty,
});
//#endregion Schemas for community records
